import { request } from "../http/api-client";

const BASE = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_PAYOUTS_BASE_URL) || "http://localhost:3099";

export interface PlatformOverview {
  totalMerchants: number;
  activeMerchants: number;
  pendingMerchants: number;
  suspendedMerchants: number;
  totalPlatformVolume: number; // Fixed: was string, now number from API
  platformSuccessRate: number;
  todayPayinVolume: number;
  todayPayoutVolume: number;
  todayPayinCount: number;
  todayPayoutCount: number;
  todayTotalVolume: number; // New field for "totalVolume" from API response
  todaySuccessRate: number; // New field for today's success rate
}

export interface WebhookStatus {
  totalWebhooks: number;
  successfulDeliveries: number;
  failedDeliveries: number;
  averageDeliveryTime: number;
}

export interface PSPProvider {
  name: string;
  status: "HEALTHY" | "DEGRADED" | "DOWN";
  uptime: number;
}

export interface APIPerformance {
  averageResponseTime: number;
  requestsPerSecond: number;
  errorRate: number;
}

export interface SystemHealth {
  webhookStatus: WebhookStatus;
  pspHealth: {
    providers: PSPProvider[];
  };
  apiPerformance: APIPerformance;
}

export interface PayoutOperations {
  pendingApprovals: number;
  approvedToday: number;
  rejectedToday: number;
  totalPayoutVolume: number;
}

export interface WalletReservations {
  totalReserved: number;
  criticalReservations: number;
  expiringSoon: number;
}

export interface RevenueTrend {
  month: string;
  revenue: number;
}

export interface PlatformRevenue {
  dailyRevenue: number;
  monthlyRevenue: number;
  revenueTrends: RevenueTrend[];
}

export interface FinancialOversight {
  payoutOperations: PayoutOperations;
  walletReservations: WalletReservations;
  platformRevenue: PlatformRevenue;
}

export interface DailyTransactionData {
  date: string;
  payinCount: number;
  payoutCount: number;
  payinVolume: number;
  payoutVolume: number;
}

export interface AdminDashboardData {
  platformOverview: PlatformOverview;
  systemHealth: SystemHealth;
  financialOversight: FinancialOversight;
  dailyTransactionData: DailyTransactionData[];
  lastUpdated: string;
}

export interface AdminDashboardResponse {
  success: boolean;
  data: AdminDashboardData;
  message: string;
  timestamp: string;
}

/**
 * Fetch admin dashboard data with comprehensive platform metrics
 */
export async function fetchAdminDashboard(): Promise<AdminDashboardData> {
  const url = `${BASE}/api/v1/merchants/admin/dashboard`;
  const res = await request<AdminDashboardResponse>(url, { method: "GET" });

  // Fetch today's transaction stats for the new real-time cards
  try {
    const transactionStatsUrl = `${BASE}/api/v1/transactions/stats?period=today`;
    const transactionStatsRes = await request<TransactionStatsResponse>(transactionStatsUrl, { method: "GET" });

    // Enhance platform overview with today's payin/payout data
    const todayStats = transactionStatsRes.data.today;
    res.data.platformOverview.todayPayinVolume = safeNumber(todayStats.totalPayinSuccess);
    res.data.platformOverview.todayPayoutVolume = safeNumber(todayStats.totalPayoutSuccess);
    res.data.platformOverview.todayTotalVolume = safeNumber(todayStats.totalVolume); // Today Volume field
    res.data.platformOverview.todaySuccessRate = safeNumber(todayStats.successRate); // Today's success rate
    res.data.platformOverview.todayPayinCount = Math.ceil(safeNumber(todayStats.totalCount) * 0.7); // Estimate: 70% payins
    res.data.platformOverview.todayPayoutCount = Math.ceil(safeNumber(todayStats.totalCount) * 0.3); // Estimate: 30% payouts
  } catch (error) {
    console.error('Failed to fetch today\'s transaction stats:', error);
    // Set default values if fetch fails
    res.data.platformOverview.todayPayinVolume = 0;
    res.data.platformOverview.todayPayoutVolume = 0;
    res.data.platformOverview.todayTotalVolume = 0;
    res.data.platformOverview.todaySuccessRate = 0;
    res.data.platformOverview.todayPayinCount = 0;
    res.data.platformOverview.todayPayoutCount = 0;
  }

  return res.data;
}

/**
 * Transform admin daily transaction data to match existing chart format
 * Validates and safely transforms data for chart display
 */
export function transformAdminChartData(dailyData: DailyTransactionData[]) {
  if (!dailyData || !Array.isArray(dailyData)) {
    return [];
  }

  return dailyData
    .filter(item => {
      // Filter out invalid items
      if (!item || !item.date) {
        return false;
      }
      return true;
    })
    .map(item => {
      try {
        // Parse date safely
        const date = new Date(item.date);
        if (isNaN(date.getTime())) {
          return {
            date: item.date, // Use raw date if parsing fails
            payin: safeNumber(item.payinVolume),
            payout: safeNumber(item.payoutVolume),
            payinCount: safeNumber(item.payinCount),
            payoutCount: safeNumber(item.payoutCount)
          };
        }

        return {
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          payin: safeNumber(item.payinVolume),
          payout: safeNumber(item.payoutVolume),
          payinCount: safeNumber(item.payinCount),
          payoutCount: safeNumber(item.payoutCount)
        };
      } catch (error) {
        return {
          date: String(item.date),
          payin: 0,
          payout: 0,
          payinCount: 0,
          payoutCount: 0
        };
      }
    });
}

/**
 * Transform admin revenue trends for chart display
 */
export function transformRevenueTrends(revenueTrends: RevenueTrend[]) {
  return revenueTrends.map(item => ({
    month: new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    revenue: item.revenue
  }));
}

/**
 * Get system health status color based on metrics
 */
export function getSystemHealthStatus(systemHealth: SystemHealth): "healthy" | "warning" | "critical" {
  const { webhookStatus, apiPerformance, pspHealth } = systemHealth;

  // Check for critical issues
  if (apiPerformance.errorRate > 5 || webhookStatus.failedDeliveries > 0) {
    return "critical";
  }

  // Check for warnings
  if (apiPerformance.errorRate > 1 || webhookStatus.averageDeliveryTime > 1000) {
    return "warning";
  }

  // Check PSP health
  const unhealthyProviders = pspHealth.providers.filter(p => p.status !== "HEALTHY");
  if (unhealthyProviders.length > 0) {
    return unhealthyProviders.some(p => p.status === "DOWN") ? "critical" : "warning";
  }

  return "healthy";
}

/**
 * Get webhook delivery success rate percentage
 */
export function getWebhookSuccessRate(webhookStatus: WebhookStatus): number {
  if (webhookStatus.totalWebhooks === 0) return 100;
  return ((webhookStatus.successfulDeliveries / webhookStatus.totalWebhooks) * 100);
}

/**
 * Format currency amount for display
 */
export function formatCurrency(amount: number | string | undefined | null): string {
  if (amount === null || amount === undefined) return '₹0';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num) || !isFinite(num)) return '₹0';
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(0)}K`;
  return `₹${num.toFixed(0)}`;
}

/**
 * Safe number conversion for API responses
 */
export function safeNumber(value: any): number {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
}

/**
 * Safe string property access
 */
export function safeString(value: any): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * Fetch admin transaction stats for platform-wide metrics
 */
export async function getAdminTransactionStats(period: "today" | "week" | "month" | "last90d" = "today") {
  const url = `${BASE}/api/v1/transactions/stats?period=${period}`;
  const res = await request<{ success: boolean; data: any }>(url, { method: "GET" });
  const data = res.data || {};
  const keyMap: Record<string, string> = {
    today: "today",
    week: "thisWeek",
    month: "thisMonth",
    last90d: "last90Days",
  };
  const k = keyMap[period];
  const s = data[k] || {};
  return {
    totalAmount: s.totalAmount || 0,
    successRate: s.successRate || 0,
    successAmount: s.totalPayinSuccess || 0,
    payinAmount: s.totalVolume || s.totalAmount || 0, // Fixed: use totalVolume from API response
    payoutAmount: s.totalPayoutSuccess || 0,
  };
}

// New Transaction API Interfaces and Functions

export interface TransactionStats {
  totalCount: number;
  totalAmount: number;
  successRate: number;
  averageAmount: number;
  totalVolume: number;
  totalPayinSuccess: number;
  totalFee: number;
  totalPayoutSuccess: number;
}

export interface TransactionStatsResponse {
  success: boolean;
  data: {
    today: TransactionStats;
    yesterday: TransactionStats;
    thisWeek: TransactionStats;
    lastWeek: TransactionStats;
    thisMonth: TransactionStats;
    lastMonth: TransactionStats;
    last90Days: TransactionStats;
    trends: {
      volumeGrowth: number;
      revenueGrowth: number;
      successRateChange: number;
    };
    topPaymentMethods: any[];
    hourlyDistribution: any[];
    recentLargeTransactions: any[];
    recentFailures: any[];
  };
  timestamp: string;
}

export interface RecentTransaction {
  id: string;
  merchantId: string;
  reference: string;
  type: "PAYIN" | "PAYOUT";
  status: "PENDING" | "SUCCESS" | "FAILED" | "EXPIRED" | "CANCELLED" | "PROCESSING";
  amount: number;
  currency: string;
  feeAmount: number;
  netAmount: number;
  providerReference?: string;
  providerTransactionId?: string;
  providerResponse?: any;
  customer: any;
  paymentMethod?: string;
  idempotencyKey: string;
  webhookDelivered: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
  metadata?: any;
  isDeleted: boolean;
}

export interface RecentTransactionsResponse {
  success: boolean;
  data: {
    transactions: RecentTransaction[];
    summary: {
      totalCount: number;
      totalAmount: number;
      totalFees: number;
      totalNet: number;
      successCount: number;
      successAmount: number;
      successRate: number;
      failedCount: number;
      failedAmount: number;
      failureRate: number;
      pendingCount: number;
      pendingAmount: number;
      refundCount: number;
      refundAmount: number;
      byStatus: Record<string, { count: number; amount: number; percentage: number }>;
      byType: Record<string, { count: number; amount: number; percentage: number }>;
      byPaymentMethod: Record<string, { count: number; amount: number; percentage: number }>;
      byDate: any[];
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  timestamp: string;
}

/**
 * Fetch transaction statistics for a specific period
 */
export async function fetchTransactionStats(period: "today" | "week" | "month" | "last90d" = "today"): Promise<TransactionStatsResponse['data']> {
  const url = `${BASE}/api/v1/transactions/stats?period=${period}`;
  const res = await request<TransactionStatsResponse>(url, { method: "GET" });
  return res.data;
}

/**
 * Fetch recent transactions with pagination
 */
export async function fetchRecentTransactions(page: number = 1, limit: number = 10): Promise<RecentTransactionsResponse['data']> {
  const url = `${BASE}/api/v1/transactions?page=${page}&limit=${limit}`;
  const res = await request<RecentTransactionsResponse>(url, { method: "GET" });
  return res.data;
}

/**
 * Transform transaction stats data to DailyTransactionData format for chart usage
 */
export function transformTransactionStatsToDailyData(statsResponse: TransactionStatsResponse['data'], targetPeriod: "today" | "thisWeek" | "thisMonth" | "last90Days" = "today"): DailyTransactionData[] {
  const stats = statsResponse[targetPeriod];

  // Create DailyTransactionData from stats (single day entry)
  return [
    {
      date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      payinVolume: safeNumber(stats.totalPayinSuccess),
      payoutVolume: safeNumber(stats.totalPayoutSuccess),
      payinCount: Math.ceil(safeNumber(stats.totalCount) * 0.7), // Estimate: 70% payins
      payoutCount: Math.ceil(safeNumber(stats.totalCount) * 0.3), // Estimate: 30% payouts
    }
  ];
}

/**
 * Get daily transaction data from transaction list for better chart visualization
 */
export function transformTransactionsToDailyData(transactions: RecentTransaction[]): DailyTransactionData[] {
  // Group transactions by date
  const groupedByDate = transactions.reduce((acc, transaction) => {
    const date = new Date(transaction.createdAt).toISOString().split('T')[0];
    if (!acc[date]) {
      acc[date] = { payinVolume: 0, payoutVolume: 0, payinCount: 0, payoutCount: 0 };
    }

    if (transaction.type === 'PAYIN') {
      acc[date].payinVolume += transaction.amount;
      acc[date].payinCount += 1;
    } else if (transaction.type === 'PAYOUT') {
      acc[date].payoutVolume += transaction.amount;
      acc[date].payoutCount += 1;
    }

    return acc;
  }, {} as Record<string, { payinVolume: number; payoutVolume: number; payinCount: number; payoutCount: number }>);

  // Convert to DailyTransactionData array
  return Object.entries(groupedByDate).map(([date, data]) => ({
    date,
    payinVolume: data.payinVolume,
    payoutVolume: data.payoutVolume,
    payinCount: data.payinCount,
    payoutCount: data.payoutCount
  })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}